import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import _ from 'lodash';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { LongRunningContextCategoryTyp } from 'src/app/samwebDescription/samweb/waitingContext/waitingContextCategory';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { ChangeOwnPasswordResponseData, UsersService } from 'src/generated/swagger';
import { LongRunningContextState } from '../../server-communication/running-actions/long-running-context.state';
import { EnabledFeaturesState } from '../../session/enabled-features/enabled-features.state';
import { SessionState } from '../../session/session.state';
import { ResetPasswordEditing, ChangeOwnPasswordNow, UpdateConfirmedPassword, UpdateCurrentPassword, UpdateNewPassword } from './change-own-password.actions';

export interface ChangeOwnPasswordStateModel {
   currentPassword: string;
   newPassword: string;
   confirmedPassword: string;
   lastChangeSucceeded: boolean;
   lastChangeMessage: string;
}
@State<ChangeOwnPasswordStateModel>({
   name: 'changeOwnPasswordState',
   defaults: {
      currentPassword: '',
      newPassword: '',
      confirmedPassword: '',
      lastChangeSucceeded: true,
      lastChangeMessage: '',
   },
})
@Injectable()
export class ChangeOwnPasswordState {
   constructor(private usersService: UsersService) {}

   @Selector([ChangeOwnPasswordState])
   public static currentPassword(state: ChangeOwnPasswordStateModel) {
      return state.currentPassword;
   }

   @Selector([ChangeOwnPasswordState])
   public static newPassword(state: ChangeOwnPasswordStateModel) {
      return state.newPassword;
   }

   @Selector([ChangeOwnPasswordState])
   public static confirmedPassword(state: ChangeOwnPasswordStateModel) {
      return state.confirmedPassword;
   }

   @Selector([ChangeOwnPasswordState])
   public static lastChangeSucceeded(state: ChangeOwnPasswordStateModel) {
      return state.lastChangeSucceeded;
   }

   @Selector([ChangeOwnPasswordState])
   public static lastChangeMessage(state: ChangeOwnPasswordStateModel) {
      return state.lastChangeMessage;
   }

   @Selector([SessionState.canChangePassword, EnabledFeaturesState.isFeatureEnabled(FeatureEnum.ALLOW_TO_CHANGE_OWN_PASSWORD)])
   public static canChangeOwnPassword(canChangePassword: boolean, allowedToChangeOwnPassword: boolean) {
      return canChangePassword && allowedToChangeOwnPassword;
   }

   @Selector([ChangeOwnPasswordState])
   public static isCurrentPasswordSpecified(state: ChangeOwnPasswordStateModel) {
      return !_.isEmpty(state.currentPassword);
   }

   @Selector([ChangeOwnPasswordState])
   public static isNewPasswordSpecified(state: ChangeOwnPasswordStateModel) {
      return !_.isEmpty(state.newPassword);
   }

   @Selector([ChangeOwnPasswordState, ChangeOwnPasswordState.isNewPasswordSpecified])
   public static isNewPasswordConfirmed(state: ChangeOwnPasswordStateModel, isNewPasswordSpecified: boolean) {
      return isNewPasswordSpecified && state.newPassword === state.confirmedPassword;
   }

   @Selector([
      ChangeOwnPasswordState.isCurrentPasswordSpecified,
      ChangeOwnPasswordState.isNewPasswordConfirmed,
      LongRunningContextState.isLongRunningContextInProgressForCategory,
   ])
   public static canChangePasswordNow(
      isCurrentPasswordSpecified: boolean,
      isNewPasswordConfirmed: boolean,
      isLongRunningContextInProgressForCategoryFn: (category: LongRunningContextCategoryTyp) => boolean
   ) {
      return (
         isCurrentPasswordSpecified && isNewPasswordConfirmed && !isLongRunningContextInProgressForCategoryFn(LongRunningContextCategoryTyp.CHANGE_PASSWORD)
      );
   }

   @Selector([ChangeOwnPasswordState, LongRunningContextState.isLongRunningContextInProgressForCategory])
   public static isResultAvailable(
      state: ChangeOwnPasswordStateModel,
      isLongRunningContextInProgressForCategoryFn: (category: LongRunningContextCategoryTyp) => boolean
   ) {
      return !_.isEmpty(state.lastChangeMessage) && !isLongRunningContextInProgressForCategoryFn(LongRunningContextCategoryTyp.CHANGE_PASSWORD);
   }

   @Action(UpdateCurrentPassword)
   public updateCurrentPassword(ctx: StateContext<ChangeOwnPasswordStateModel>, action: UpdateCurrentPassword) {
      ctx.patchState({
         currentPassword: action.currentPassword,
      });
   }

   @Action(UpdateNewPassword)
   public updateNewPassword(ctx: StateContext<ChangeOwnPasswordStateModel>, action: UpdateNewPassword) {
      ctx.patchState({
         newPassword: action.newPassword,
      });
   }

   @Action(UpdateConfirmedPassword)
   public updateConfirmedPassword(ctx: StateContext<ChangeOwnPasswordStateModel>, action: UpdateConfirmedPassword) {
      ctx.patchState({
         confirmedPassword: action.confirmedPassword,
      });
   }

   @Action(ResetPasswordEditing)
   public resetPasswordEditing(ctx: StateContext<ChangeOwnPasswordStateModel>, action: ResetPasswordEditing) {
      ctx.patchState({
         currentPassword: '',
         newPassword: '',
         confirmedPassword: '',
         lastChangeSucceeded: true,
         lastChangeMessage: '',
      });
   }

   @Action(ChangeOwnPasswordNow)
   public changeOwnPasswordNow(ctx: StateContext<ChangeOwnPasswordStateModel>, action: ChangeOwnPasswordNow) {
      const state = ctx.getState();

      return this.usersService
         .changeOwnPassword({
            currentPassword: state.currentPassword,
            newPassword: state.newPassword,
         })
         .pipe(
            tap(() => ctx.dispatch(new ResetPasswordEditing())),
            tap(() =>
               ctx.patchState({
                  lastChangeSucceeded: true,
                  lastChangeMessage: 'common.change-password.success-message',
               })
            ),
            catchError((error: HttpErrorResponse) => {
               const resultData = error.error as ChangeOwnPasswordResponseData;
               const errorMessage = resultData?.failureReason;

               ctx.patchState({
                  lastChangeSucceeded: false,
                  lastChangeMessage: errorMessage ?? 'common.change-password.error-message',
               });

               return of(null);
            })
         );
   }
}
